import { unsConstants } from 'constants/index';
import { INotification } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { unsServices } from 'services';

const {
  NOTIFICATION_POPUP_PAGE_SIZE,
  NOTIFICATION_MODAL_PAGE_SIZE,
} = unsConstants;

const useNotifications = (appId?: string) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [loading, setLoading] = useState(false);

  // Refreshing the badge is a background touchpoint, so it fetches with
  // bypassError: true — a failed count must never surface a global error toast.
  // The list-facing fetchList below keeps normal error reporting.
  const refreshUnread = useCallback(async () => {
    if (!appId) return;
    try {
      const data = await unsServices.getMessages(
        { app_id: appId, limit: NOTIFICATION_POPUP_PAGE_SIZE },
        { bypassError: true }
      );
      if (typeof data.unread === 'number') {
        setUnreadCount(data.unread);
      }
    } catch (error) {
      console.error('Error while counting unread notifications', error);
    }
  }, [appId]);

  const fetchList = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const data = await unsServices.getMessages({
        app_id: appId,
        limit: NOTIFICATION_POPUP_PAGE_SIZE,
      });
      setNotifications(data.notifications || []);
      if (typeof data.unread === 'number') {
        setUnreadCount(data.unread);
      }
    } catch (error) {
      console.error('Error while fetching notifications', error);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  const markRead = useCallback(async (notification: INotification) => {
    if (notification.is_read) return;
    try {
      await unsServices.markRead(notification.id);
      setNotifications(prev =>
        prev.map(item =>
          item.id === notification.id ? { ...item, is_read: true } : item
        )
      );
      setUnreadCount(prev => Math.max(prev - 1, 0));
    } catch (error) {
      console.error('Error while marking notification as read', error);
    }
  }, []);

  const markReadAll = useCallback(async () => {
    if (!appId) return;
    try {
      await unsServices.markReadAll({ app_id: appId });
      setNotifications(prev => prev.map(item => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error while marking all notifications as read', error);
    }
  }, [appId]);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread]);

  return {
    unreadCount,
    notifications,
    loading,
    fetchList,
    refreshUnread,
    markRead,
    markReadAll,
  };
};

/**
 * State for the "view all notifications" modal. Kept separate from
 * useNotifications so its paginated (limit 20) list never clobbers the
 * 3-item popover list. Read-state mutations call onAfterChange so the bell
 * badge (owned by useNotifications) stays in sync.
 */
const useNotificationHistory = (
  appId?: string,
  open?: boolean,
  onAfterChange?: () => void
) => {
  const [items, setItems] = useState<INotification[]>([]);
  const [checkPoint, setCheckPoint] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const fetchMessages = useCallback(
    async (cursor?: string) => {
      if (!appId) return;
      const isFirst = !cursor;
      isFirst ? setLoading(true) : setLoadingMore(true);
      try {
        const data = await unsServices.getMessages({
          app_id: appId,
          limit: NOTIFICATION_MODAL_PAGE_SIZE,
          ...(cursor ? { check_point: cursor } : {}),
        });
        const list = data.notifications || [];
        setItems(prev => (isFirst ? list : [...prev, ...list]));
        setCheckPoint(data.check_point);
        setHasMore(!!data.check_point);
      } catch (error) {
        console.error('Error while fetching notification history', error);
      } finally {
        isFirst ? setLoading(false) : setLoadingMore(false);
      }
    },
    [appId]
  );

  // Reload from the first page each time the modal is opened.
  useEffect(() => {
    if (open && appId) {
      fetchMessages();
    }
  }, [open, appId, fetchMessages]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchMessages(checkPoint);
  }, [loading, loadingMore, hasMore, checkPoint, fetchMessages]);

  const markRead = useCallback(
    async (notification: INotification) => {
      if (notification.is_read) return;
      try {
        await unsServices.markRead(notification.id);
        setItems(prev =>
          prev.map(item =>
            item.id === notification.id ? { ...item, is_read: true } : item
          )
        );
        onAfterChange?.();
      } catch (error) {
        console.error('Error while marking notification as read', error);
      }
    },
    [onAfterChange]
  );

  const markReadAll = useCallback(async () => {
    if (!appId) return;
    try {
      await unsServices.markReadAll({ app_id: appId });
      setItems(prev => prev.map(item => ({ ...item, is_read: true })));
      onAfterChange?.();
    } catch (error) {
      console.error('Error while marking all notifications as read', error);
    }
  }, [appId, onAfterChange]);

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    markRead,
    markReadAll,
  };
};

export default {
  useNotifications,
  useNotificationHistory,
};
